import { useMemo } from 'react'
import { useGetCurrentLanguage } from 'src/store/game'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCheckInSteps } from '../store'
import { type CheckInStatus, isBoosterReward } from '../types/domain'

interface CheckInItemData {
  step: number
  title: string
  boostPercent: number
  boostLabel: string
  status: CheckInStatus
  cta?: React.ReactNode
}

export const useDailyCheckInItems = (currentStep: number): CheckInItemData[] => {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const steps = useGetCheckInSteps()

  return useMemo(() => {
    return steps.map((step) => {
      let status: CheckInItemData['status'] = 'locked'

      if (step.received) {
        switch (step.received.status) {
          case 'claimable':
            status = 'unused'
            break
          case 'claimed':
            if (!step.received.id) {
              status = 'locked'
              break
            }

            if (isBoosterReward(step.reward) && step.reward.options.activationType === 'auto') {
              status = 'auto'
              break
            }

            status = 'unused'
            break
          case 'applied':
            status = 'used'
            break
          case 'used':
            status = 'used'
            break
          case 'expired':
            status = 'expired'
            break
        }
      } else if (step.step < currentStep) {
        // Past steps that weren't claimed (shouldn't happen in normal flow)
        status = 'used'
      } else if (step.step === currentStep) {
        // Current step ready to claim
        status = 'unused'
      } else {
        // Future steps
        status = 'locked'
      }

      // Format boost value based on reward type
      let boostPercent = 0
      let boostLabel = 'Reward'

      if (isBoosterReward(step.reward)) {
        boostPercent = step.reward.options.boost.value
        boostLabel = t('Multiplier Boost')
      } else {
        boostLabel = t('Collectible')
      }

      return {
        step: step.step,
        title: t("Day {{day}}", { day: step.step }),
        boostPercent,
        boostLabel,
        status
      }
    })
  }, [steps, currentStep, t])
}
